import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { CheckCircle, HorizontalRule, Settings, Warning } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Analysis, AnalysisStatus } from "../../../models/general/analysis";
import { URLS } from "../../../services/app-urls";
import { BaseService } from "../../../services/base-service";
import { translate } from "../../../translate/translate";
import { parseLocaleNumber } from "../../../utils/locale-number-utils";
import InfoCardComponent from "./components/info-card.component";
import PoStatusCardComponent from "./components/po-status-card.component";
import AnalysisSelectorComponent from "./components/analysis-selector.component";
import UpcomingDeliveriesComponent from "./components/upcoming-deliveries.component";
import SapIntegrationHealthComponent from "./components/sap-integration-health.component";

interface DashboardMetrics {
  critical?: number | null;
  attention?: number | null;
  stable?: number | null;
  obsolete?: number | null;
  analysis_id?: number;
}

interface DashboardMetricsResponse {
  cards: DashboardMetrics;
}

/** Completed analyses offered in the selector, newest first. */
const ANALYSES_LIMIT = 10;

const DashboardPageComponent = () => {
  const service = useMemo(() => new BaseService<Analysis>(URLS.ANALYSIS_IA), []);
  const analysisService = useMemo(() => new BaseService<Analysis>(URLS.ANALYSIS), []);
  const [dataCards, setDataCards] = useState<DashboardMetrics>();
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);

  useEffect(() => {
    analysisService.clearParameters();
    analysisService.addParameter("status", AnalysisStatus.COMPLETED);
    analysisService.addParameter("is_fully_completed", true);
    analysisService.addParameter("ordering", "-created_at");
    analysisService.addParameter("limit", ANALYSES_LIMIT);
    analysisService.getAllPaginate()
      .then(response => {
        const results = response.data.results ?? [];
        setAnalyses(results);
        // Default selection: the most recent Full analysis (the one run with every
        // BOM). Falls back to the newest completed analysis when there is no Full.
        setSelectedAnalysis(results.find(item => item.analysis_type === "full") ?? results[0] ?? null);
      })
      .catch(() => setAnalyses([]));
  }, [analysisService]);

  useEffect(() => {
    setIsLoadingMetrics(true);
    service.clearParameters();
    if (selectedAnalysis?.id) service.addParameter("analysis", selectedAnalysis.id);
    service.getFromListRoute("metrics")
      .then(response => {
        const responseData = response.data as unknown as DashboardMetricsResponse;
        setDataCards(responseData.cards);
      })
      .catch(error => toast.error(translate.t("unknown_error"), error))
      .finally(() => setIsLoadingMetrics(false));
  }, [service, selectedAnalysis]);

  const formatValue = (value: number | null | undefined): string | null => {
    if (value === null || value === undefined || typeof value !== "number") return null;
    return parseLocaleNumber(Number(value));
  };

  const whichIconMetrics = (value: number | null | undefined, color: "warning" | "error") => {
    if (value === null || value === undefined) return <HorizontalRule color="action" fontSize="large" />;
    if (value <= 0) return <CheckCircle color="success" fontSize="large" />;
    if (value > 0) return <Warning color={color} fontSize="large" />;
    return <HorizontalRule color="action" fontSize="large" />;
  };

  return (
    <section>
      <div className="floating-title" style={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h5" sx={{ fontSize: "20px" }}>{translate.t("dashboard")}</Typography>
      </div>
      <div className="floating-button">
        <Button
          component={NavLink}
          to="#"
          startIcon={<Settings />}
          variant="contained"
          color="primary"
          sx={{ display: "none" }}
        >
          {translate.t("configurations")}
        </Button>
      </div>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "12px" }}>
        <Typography sx={{ fontFamily: "Inter", fontWeight: 700, fontSize: "16px", flexShrink: 0 }}>
          {translate.t("coverage_indicators")}
        </Typography>
        <AnalysisSelectorComponent
          analyses={analyses}
          value={selectedAnalysis}
          latestId={analyses[0]?.id}
          onChange={setSelectedAnalysis}
          onOpen={analysis => window.open(`/general/analysis/${analysis.id}/gds-calculation`, "_blank", "noopener,noreferrer")}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "row", gap: "15px", marginBottom: "16px" }}>
        <InfoCardComponent
          title={translate.t("coverage_critical")}
          value={formatValue(dataCards?.critical) ?? "-"}
          icon={whichIconMetrics(dataCards?.critical, "error")}
          description={translate.t("items_less_coverage")}
          isLoading={isLoadingMetrics}
          onClick={dataCards?.analysis_id ? () => window.open(`/general/analysis/${dataCards.analysis_id}/materiais-demandas?coverage=CRITICAL`, "_blank", "noopener,noreferrer") : undefined}
        />
        <InfoCardComponent
          title={translate.t("coverage_partial")}
          value={formatValue(dataCards?.attention) ?? "-"}
          icon={whichIconMetrics(dataCards?.attention, "warning")}
          description={translate.t("items_partial_coverage")}
          isLoading={isLoadingMetrics}
          onClick={dataCards?.analysis_id ? () => window.open(`/general/analysis/${dataCards.analysis_id}/materiais-demandas?coverage=ATTENTION`, "_blank", "noopener,noreferrer") : undefined}
        />
        <InfoCardComponent
          title={translate.t("coverage_total")}
          value={formatValue(dataCards?.stable) ?? "-"}
          icon={whichIconMetrics(dataCards?.stable, "warning")}
          description={translate.t("items_full_coverage")}
          isLoading={isLoadingMetrics}
          onClick={dataCards?.analysis_id ? () => window.open(`/general/analysis/${dataCards.analysis_id}/materiais-demandas?coverage=STABLE`, "_blank", "noopener,noreferrer") : undefined}
        />
        <InfoCardComponent
          title={translate.t("obsolete_materials")}
          value={formatValue(dataCards?.obsolete) ?? "-"}
          icon={whichIconMetrics(dataCards?.obsolete, "error")}
          description={translate.t("items_without_bom_link")}
          isLoading={isLoadingMetrics}
          onClick={() => window.open("/general/stock?filterObsolete=true", "_blank", "noopener,noreferrer")}
        />
      </Box>
      <Box
        sx={{
          alignItems: "stretch",
          display: "grid",
          gap: "15px",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        <Box sx={{ display: "flex", gridColumn: "1", minWidth: 0 }}>
          <PoStatusCardComponent />
        </Box>
        <Box sx={{ display: "flex", gridColumn: { xs: "1", xl: "2" }, minWidth: 0 }}>
          <UpcomingDeliveriesComponent />
        </Box>
      </Box>

      <Box sx={{ marginTop: "15px" }}>
        <SapIntegrationHealthComponent analysisId={selectedAnalysis?.id} />
      </Box>
    </section>
  );
};

export default DashboardPageComponent;
